import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CandidateLiteDto } from '../../api/role/models/CandidateDto'
import Button from '../Button'
import Modal from '../Modal'
import { faCalendar } from '@fortawesome/free-solid-svg-icons'
import roleApi from '../../api/role/RoleApi'

interface Props {
    roleId: string
    candidate: CandidateLiteDto
    onClose: () => void
}

// Define the business hours for meetings.
const WORK_DAY_START = 9 // 9 AM
const WORK_DAY_END = 17 // 5 PM

// Returns an array of Date objects for the next `count` working days,
// starting with today if it's a working day.
function getInitialWorkingDays(count: number): Date[] {
    const days: Date[] = []
    const date = new Date()
    while (days.length < count) {
        const dayOfWeek = date.getDay() // Sunday = 0, Saturday = 6
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            days.push(new Date(date))
        }
        date.setDate(date.getDate() + 1)
    }
    return days
}

// Returns the next `count` working days after the given day.
function getNextWorkingDaysAfter(day: Date, count: number): Date[] {
    const days: Date[] = []
    const date = new Date(day)
    date.setDate(date.getDate() + 1) // start from the day after the given day
    while (days.length < count) {
        const dayOfWeek = date.getDay()
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            days.push(new Date(date))
        }
        date.setDate(date.getDate() + 1)
    }
    return days
}

// For a given day, generate a list of Date objects representing 30-minute time slots
// between WORK_DAY_START and WORK_DAY_END.
function getTimeSlotsForDay(day: Date): Date[] {
    const slots: Date[] = []
    const slotDate = new Date(day)
    slotDate.setHours(WORK_DAY_START, 0, 0, 0)
    while (slotDate.getHours() < WORK_DAY_END) {
        slots.push(new Date(slotDate))
        slotDate.setMinutes(slotDate.getMinutes() + 30)
    }
    return slots
}

// Format a Date as "Monday, January 1" (or similar based on locale).
function formatDate(date: Date): string {
    return date.toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
    })
}

// Format a Date as a time string like "9:00 AM".
function formatTime(date: Date): string {
    return date.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
    })
}

export default function BookMeetingModal({
    roleId,
    candidate,
    onClose,
}: Props): React.JSX.Element {
    // Hold the selected time slots as Date objects.
    const [selectedSlots, setSelectedSlots] = useState<Date[]>([])
    // Store working days state; start with three working days (starting from today if possible).
    const [workingDays, setWorkingDays] = useState<Date[]>(
        getInitialWorkingDays(3),
    )

    // Get the current time (for comparing time slots on the current day)
    const now = new Date()

    // Toggle a single slot's selection state.
    const toggleSlotSelection = (slot: Date, isDisabled: boolean) => {
        if (isDisabled) return
        setSelectedSlots((prev) => {
            if (prev.some((s) => s.getTime() === slot.getTime())) {
                return prev.filter((s) => s.getTime() !== slot.getTime())
            } else {
                return [...prev, slot]
            }
        })
    }

    // Toggle all slots for a given day.
    // Only toggles the slots that are not in the past.
    const toggleAllForDay = (availableSlots: Date[], allSelected: boolean) => {
        if (allSelected) {
            setSelectedSlots((prev) =>
                prev.filter(
                    (slot) =>
                        !availableSlots.some(
                            (avail) => avail.getTime() === slot.getTime(),
                        ),
                ),
            )
        } else {
            setSelectedSlots((prev) => [
                ...prev,
                ...availableSlots.filter(
                    (slot) =>
                        !prev.some(
                            (selected) => selected.getTime() === slot.getTime(),
                        ),
                ),
            ])
        }
    }

    // Add three more working days after the last day in workingDays.
    const handleAddMoreDays = () => {
        const lastDay = workingDays[workingDays.length - 1]
        const newDays = getNextWorkingDaysAfter(lastDay, 3)
        setWorkingDays((prev) => [...prev, ...newDays])
    }

    // Called when the user clicks Book.
    async function handleBook() {
        await roleApi.bookInterview(
            roleId,
            candidate.candidatePersonId,
            selectedSlots,
        )
        onClose()
    }

    return (
        <Modal
            onClose={onClose}
            title="Book Interview"
            size="large"
            rightFooter={
                <Button style="primary" type="button" onClick={handleBook}>
                    <FontAwesomeIcon icon={faCalendar} className="mr-2" />
                    Book
                </Button>
            }
        >
            <div className="meeting-slots space-y-6">
                {workingDays.map((day) => {
                    const timeSlots = getTimeSlotsForDay(day)
                    // Determine if this day is today.
                    const isToday = day.toDateString() === now.toDateString()
                    // Filter out time slots that are in the past for today.
                    const availableSlots = timeSlots.filter(
                        (slot) => !isToday || slot.getTime() >= now.getTime(),
                    )
                    const allSelected = availableSlots.every((slot) =>
                        selectedSlots.some(
                            (s) => s.getTime() === slot.getTime(),
                        ),
                    )
                    return (
                        <div
                            key={day.toISOString()}
                            className="day-section pb-4 border-b border-gray-300"
                        >
                            <h3 className="text-lg font-semibold flex items-center">
                                {formatDate(day)}
                                <button
                                    onClick={() =>
                                        toggleAllForDay(
                                            availableSlots,
                                            allSelected,
                                        )
                                    }
                                    className="ml-4 text-blue-500 text-sm underline hover:text-blue-700 focus:outline-none"
                                >
                                    {allSelected
                                        ? 'Deselect All'
                                        : 'Select All'}
                                </button>
                            </h3>
                            <div className="slots-container grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 mt-2">
                                {timeSlots.map((slot) => {
                                    const timeLabel = formatTime(slot)
                                    const isPast =
                                        isToday &&
                                        slot.getTime() < now.getTime()
                                    const isSelected = selectedSlots.some(
                                        (s) => s.getTime() === slot.getTime(),
                                    )
                                    const buttonClasses = isPast
                                        ? 'px-2 py-1 border rounded bg-gray-200 text-gray-500 border-gray-200 cursor-not-allowed'
                                        : isSelected
                                        ? 'px-2 py-1 border rounded bg-blue-600 text-white border-blue-700 transition-colors'
                                        : 'px-2 py-1 border rounded bg-white text-gray-700 hover:bg-gray-100 border-gray-300 transition-colors'

                                    return (
                                        <button
                                            key={slot.toISOString()}
                                            onClick={() =>
                                                toggleSlotSelection(
                                                    slot,
                                                    isPast,
                                                )
                                            }
                                            className={buttonClasses}
                                            disabled={isPast}
                                        >
                                            {timeLabel}
                                        </button>
                                    )
                                })}
                            </div>
                        </div>
                    )
                })}
            </div>
            <div className="mt-4 text-center">
                <button
                    onClick={handleAddMoreDays}
                    className="text-blue-500 text-sm underline hover:text-blue-700 focus:outline-none"
                >
                    More Days
                </button>
            </div>
        </Modal>
    )
}
